import { useCallback, useEffect, useRef, useState } from 'react'
import { type ShapeTextEditorRef } from '@/components/app/shapes/shape-text-editor'
import type { ChainTextSelection } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-selection'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { Paragraph } from '@/api/types/stowed-element'
import { EMPTY_GRID_SNAP_RECTS } from '../constants/shape-manager-constants'
import type { ShapeManagerProps } from '../types/shape-manager-props'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { InteractionState } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { MultiSelectShapeGeomSnap } from '@/lib/transforms/types/multi-select-shape-geom-snap'
import type { PanelMultiRotateBaseline } from '@/lib/transforms/types/panel-multi-rotate-baseline'

export function useShapeManagerState(props: ShapeManagerProps) {
  const {
    className,
    onShapeClick,
    onShapeDoubleClick,
    onShapeContextMenu,
    onUserEdit,
    onShapeCreated,
    onShapeCommitted,
    onBatchShapesCommitted,
    linkedTextChains,
    getLinkedTextChains,
    onSelectShapeTextChain,
    onShapeTextChainBackspaceAtStart,
    onShapeTextChainArrowLeftAtStart,
    onShapeTextChainArrowRightAtEnd,
    onShapeTextChainOverflowCaret,
    onShapeTextLiveEdit,
    onLinkedTextExcludedFromGroup,
    onShapeDeleted,
    onSelectionChange,
    onShapeSelectionHistory,
    guides = [],
    snapToGuides = false,
    snapGuideDistance = 8,
    snapToGrid = false,
    gridSnapStep = 12,
    gridSnapLiveRects,
    unitOfMeasure = 'Picas',
    scale = 1,
    isInShapeCreationMode = false,
    isSelectionEnabled = true,
    editScopeGroupId = null,
    isolationRootGroupId = null,
    onExitGroupIsolationMode,
    smartGuidesEnabled = true,
    suppressMultiSelectChrome = false,
    isInNodeEditMode = false,
    onActiveSmartGuidesChange,
    onLiveSelectionGeometrySync,
    onLiveShapeGeometryChangeForWrap,
    onCornerResizeGestureActiveChange,
    onUnlockSelectionBadgeClick,
    onShapeTextEditEnter,
    onShapeTextEditExit,
    onShapeTextContextMenu,
    suppressShapeTextExitOnBlur = false,
    onUnfreezeSelectionBadgeClick,
    elementZIndexMap,
    textOnPathPreview,
    onTextOnPathLengthChange,
    onTextOnPathStartDistanceChange,
    defaultShapeTextSeedStyle,
  } = props
  const resolvedGridSnapLiveRects = gridSnapLiveRects ?? EMPTY_GRID_SNAP_RECTS
  /**
   * `snapGuideDistance` (default 8) is a document-space (pt) threshold that `computeSmartGuides`
   * compares directly against unscaled shape bounds — it was never adjusted for zoom. At 100%
   * that's ~8 screen px, a reasonable catch radius; but zoomed out (which is exactly what's
   * needed to reach a shape sitting out on the pasteboard, off the spread) the same 8 document
   * points shrinks to a fraction of a screen pixel, so smart guides silently stop triggering —
   * not because anything excludes off-spread shapes, but because the magnetic radius has become
   * too small to ever be crossed. Dividing by `scale` keeps the catch radius constant in SCREEN
   * pixels at any zoom, matching how a user actually perceives "close enough to snap."
   */
  const effectiveSnapGuideDistance =
    snapGuideDistance / (Number.isFinite(scale) && scale > 0 ? scale : 1)
  /**
   * True while a single-object resize drag is currently clamped at the pasteboard's max
   * dimension (`MAX_VALUE_PT`) — lets the toast fire once per "crossing into the ceiling"
   * instead of on every pointermove frame while held there. Resets itself the next time the
   * gesture (or a later one) reports a size back under the max.
   */
  const resizeMaxDimensionToastActiveRef = useRef(false)

  const [shapes, setShapes] = useState<ShapeData[]>([])
  const [selectedShapeIds, setSelectedShapeIds] = useState<string[]>([])
  const [shapeBounds, setShapeBounds] = useState<Record<string, ShapeBounds>>({})
  const [altHoveredShapeId, setAltHoveredShapeId] = useState<string | null>(null)
  const [multiSelectChrome, setMultiSelectChrome] = useState<MultiSelectChrome | null>(null)
  /** True while a resize handle drag is active; skips padded min frame so 0×0 shows stacked handles. */
  const [isResizeGestureActive, setIsResizeGestureActive] = useState(false)
  const [editingShapeTextId, setEditingShapeTextId] = useState<string | null>(null)
  const editingShapeTextIdRef = useRef<string | null>(null)
  /**
   * Which shapes are currently overflowing, as reported by their own `ShapeTextEditor` instance
   * (`onOverflowChange`). A plain ref, not React state: `TextOverflowBadgeLayer` (visual layer)
   * reads it through `getTextOverflowShapeIds` on its own animation-frame poll, the same way it
   * already reads `getSelectedShapeIds`/`getEditingShapeTextId` — so a change here needs no
   * re-render of this component, just to still be there on the poll's very next tick.
   */
  const shapeTextOverflowIdsRef = useRef<Set<string>>(new Set())
  const handleShapeTextOverflowChange = useCallback((shapeId: string, isOverflowing: boolean) => {
    const ids = shapeTextOverflowIdsRef.current

    if (isOverflowing === ids.has(shapeId)) return
    if (isOverflowing) ids.add(shapeId)
    else ids.delete(shapeId)
  }, [])
  /**
   * A text selection spanning more than one linked frame (WMPCM-9199).
   *
   * Owned here rather than in any single `ShapeTextEditor` because it is by definition not one
   * frame's state — and because only one member of a chain is ever the editing frame, so the
   * textarea `selectionStart/End` that every in-frame operation reads cannot represent it. Each
   * member receives its own slice as a prop and paints that; `null` means the selection is
   * ordinary, in-frame, and unchanged.
   */
  const [chainTextSelection, setChainTextSelection] = useState<ChainTextSelection | null>(null)
  const chainTextSelectionRef = useRef<ChainTextSelection | null>(null)
  /** Mirror of the render-body `chainFrameIdsByShapeId`, for callbacks that run outside render. */
  const chainFrameIdsByShapeIdRef = useRef<Map<string, string[]>>(new Map())

  chainTextSelectionRef.current = chainTextSelection
  const [shapeTextCaretClientPoint, setShapeTextCaretClientPoint] = useState<{
    x: number
    y: number
  } | null>(null)
  /**
   * Character offset to place the caret at on entering shape text edit, in the shape-text
   * convention (`paragraphsToPlainText`). Set by a chain hand-off — backspace/arrow across a link
   * boundary, or overflow flowing into this shape — where the destination is a character position,
   * not a click point (WMPCM-9070). Null means "use `shapeTextCaretClientPoint`, or end of text".
   */
  const [shapeTextCaretOffset, setShapeTextCaretOffset] = useState<number | null>(null)
  const editStartShapeTextParagraphsRef = useRef<Paragraph[] | null>(null)
  /** WMPCM-7778: debounced live commit while shape text is being typed (mirrors use-content-commit). */
  const shapeTextCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingShapeTextCommitShapeIdRef = useRef<string | null>(null)
  /**
   * A live chain projection moved text out of (or into) the frame being edited, so a commit is owed
   * even if this frame's own paragraphs now match its edit-start baseline exactly.
   *
   * That case is not exotic — it is what typing one character at the end of an already-full chain
   * member does: the projection flows the character onward and trims this frame straight back to
   * what it held before. Without this flag the baseline diff in `commitShapeTextNow` /
   * `exitShapeTextEdit` reads that as "nothing happened", the owner's `redistributeChainStory`
   * never runs, and the character lives only in the downstream frame's in-memory model — gone on
   * reload.
   */
  const shapeTextChainProjectedRef = useRef(false)
  /** Re-entrancy guard for the live chain projection — see `handleShapeTextParagraphsChange`. */
  const shapeTextProjectionInFlightRef = useRef(false)
  const shapeTextEditorRef = useRef<ShapeTextEditorRef | null>(null)
  const [geometryBlockedPulseKey, setGeometryBlockedPulseKey] = useState(0)
  const [frozenOutlinePulse, setFrozenOutlinePulse] = useState<{
    shapeId: string
    key: number
  } | null>(null)
  const interactionRef = useRef<InteractionState | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const scaleRef = useRef(scale)
  const interactionEndTimeRef = useRef<number>(0)
  const shapesRef = useRef<ShapeData[]>([])

  const getLinkedTextChainsRef = useRef(getLinkedTextChains)
  const onLinkedTextExcludedFromGroupRef = useRef(onLinkedTextExcludedFromGroup)

  // Assigned during render, not in an effect: `liveChains` below falls back to this getter while
  // rendering, and an effect-time sync would leave that fallback one commit behind (WMPCM-6867).
  // Idempotent write of the current prop, so a double-render is harmless.
  getLinkedTextChainsRef.current = getLinkedTextChains

  useEffect(() => {
    onLinkedTextExcludedFromGroupRef.current = onLinkedTextExcludedFromGroup
  }, [onLinkedTextExcludedFromGroup])

  const ctrlCycleRef = useRef<{
    key: string
    stackSignature: string
    nextIndex: number
  } | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const shapeBoundsRef = useRef<Record<string, ShapeBounds>>({})
  /** Last known placement size per shape; drop path bounds when W/H change so chrome uses the frame until SVG remeasures. */
  const shapePlacementDimsRef = useRef<Record<string, { w: number; h: number }>>({})
  const multiSelectChromeRef = useRef<MultiSelectChrome | null>(null)
  /** Prior geometry for rigid-translation detection while the multi-select box is oriented (non-zero rotation). */
  const multiSelectSyncPrevShapesRef = useRef<Record<string, MultiSelectShapeGeomSnap> | null>(null)
  const multiSelectSelectionKeyRef = useRef<string>('')
  const pendingPointerRef = useRef<{
    dx: number
    dy: number
    clientX: number
    clientY: number
    shiftKey: boolean
  } | null>(null)
  /** Object panel "Constrain Width and Height" — when true, edge resize handles scale proportionally. */
  const panelDimensionsLinkedRef = useRef(false)
  const panelMultiRotateBaselineRef = useRef<PanelMultiRotateBaseline | null>(null)
  const pendingRotateChromeBeforeRef = useRef<MultiSelectChrome | null>(null)
  /** Prior `document.body.style.cursor`; restored after multi-move or resize drag. */
  const savedBodyCursorForMultiMoveRef = useRef<string | null>(null)
  const selectedShapeIdsRef = useRef<string[]>([])
  const onLiveSelectionGeometrySyncRef = useRef(onLiveSelectionGeometrySync)
  const onLiveShapeGeometryChangeForWrapRef = useRef(onLiveShapeGeometryChangeForWrap)
  const onCornerResizeGestureActiveChangeRef = useRef(onCornerResizeGestureActiveChange)
  const liveWrapSyncRafIdRef = useRef<number | null>(null)
  const getSnappedNudgeItemsRef = useRef<ShapeManagerRef['getSnappedNudgeItems']>(() => [])
  // Keep window listener identities stable across selection-driven re-renders. Without this
  // bridge, a cold click on a shape can swap callback identities and strand an active drag.
  const handlePointerMoveRef = useRef<(event: PointerEvent) => void>(() => {})
  const handlePointerUpRef = useRef<(event: PointerEvent) => void>(() => {})
  const handlePointerMoveBridge = useCallback((event: PointerEvent) => {
    handlePointerMoveRef.current(event)
  }, [])
  const handlePointerUpBridge = useCallback((event: PointerEvent) => {
    handlePointerUpRef.current(event)
  }, [])
  // Pointer move/up run before the gesture-start hook that owns Alt+drag duplicate, so they reach
  // it through this bridge (assigned by `useGestureStart`).
  const promoteAltDragDuplicateRef = useRef<() => void>(() => {})

  useEffect(() => {
    onLiveSelectionGeometrySyncRef.current = onLiveSelectionGeometrySync
  }, [onLiveSelectionGeometrySync])

  useEffect(() => {
    onLiveShapeGeometryChangeForWrapRef.current = onLiveShapeGeometryChangeForWrap
  }, [onLiveShapeGeometryChangeForWrap])

  useEffect(() => {
    onCornerResizeGestureActiveChangeRef.current = onCornerResizeGestureActiveChange
  }, [onCornerResizeGestureActiveChange])

  /** Defer so we never call Home's `setActiveObjectId` / context updates nested in ShapeManager's commit. */
  const scheduleLiveSelectionGeometrySync = useCallback(() => {
    queueMicrotask(() => {
      onLiveSelectionGeometrySyncRef.current?.()
    })
  }, [])

  /** Coalesces rapid live move/resize/rotate updates into at most one wrap-sync notification per
   *  animation frame — cheap for the common case of no nearby wrap-affected text, and avoids
   *  forcing a text re-render (and its wrap recompute) more often than the browser can paint
   *  anyway. Independent of `scheduleLiveSelectionGeometrySync`'s own microtask timing so this
   *  can't accidentally throttle or delay that unrelated sync. */
  const scheduleLiveWrapSync = useCallback(() => {
    if (liveWrapSyncRafIdRef.current != null) return
    liveWrapSyncRafIdRef.current = window.requestAnimationFrame(() => {
      liveWrapSyncRafIdRef.current = null
      onLiveShapeGeometryChangeForWrapRef.current?.()
    })
  }, [])

  useEffect(() => {
    scaleRef.current = scale || 1
  }, [scale])

  useEffect(() => {
    shapesRef.current = shapes
  }, [shapes])

  return {
    className,
    onShapeClick,
    onShapeDoubleClick,
    onShapeContextMenu,
    onUserEdit,
    onShapeCreated,
    onShapeCommitted,
    onBatchShapesCommitted,
    linkedTextChains,
    getLinkedTextChains,
    onSelectShapeTextChain,
    onShapeTextChainBackspaceAtStart,
    onShapeTextChainArrowLeftAtStart,
    onShapeTextChainArrowRightAtEnd,
    onShapeTextChainOverflowCaret,
    onShapeTextLiveEdit,
    onLinkedTextExcludedFromGroup,
    onShapeDeleted,
    onSelectionChange,
    onShapeSelectionHistory,
    guides,
    snapToGuides,
    snapGuideDistance,
    snapToGrid,
    gridSnapStep,
    gridSnapLiveRects,
    unitOfMeasure,
    scale,
    isInShapeCreationMode,
    isSelectionEnabled,
    editScopeGroupId,
    isolationRootGroupId,
    onExitGroupIsolationMode,
    smartGuidesEnabled,
    suppressMultiSelectChrome,
    isInNodeEditMode,
    onActiveSmartGuidesChange,
    onLiveSelectionGeometrySync,
    onLiveShapeGeometryChangeForWrap,
    onCornerResizeGestureActiveChange,
    onUnlockSelectionBadgeClick,
    onShapeTextEditEnter,
    onShapeTextEditExit,
    onShapeTextContextMenu,
    suppressShapeTextExitOnBlur,
    onUnfreezeSelectionBadgeClick,
    elementZIndexMap,
    textOnPathPreview,
    onTextOnPathLengthChange,
    onTextOnPathStartDistanceChange,
    defaultShapeTextSeedStyle,
    resolvedGridSnapLiveRects,
    effectiveSnapGuideDistance,
    resizeMaxDimensionToastActiveRef,
    shapes,
    setShapes,
    selectedShapeIds,
    setSelectedShapeIds,
    shapeBounds,
    setShapeBounds,
    altHoveredShapeId,
    setAltHoveredShapeId,
    multiSelectChrome,
    setMultiSelectChrome,
    isResizeGestureActive,
    setIsResizeGestureActive,
    editingShapeTextId,
    setEditingShapeTextId,
    editingShapeTextIdRef,
    shapeTextOverflowIdsRef,
    handleShapeTextOverflowChange,
    chainTextSelection,
    setChainTextSelection,
    chainTextSelectionRef,
    chainFrameIdsByShapeIdRef,
    shapeTextCaretClientPoint,
    setShapeTextCaretClientPoint,
    shapeTextCaretOffset,
    setShapeTextCaretOffset,
    editStartShapeTextParagraphsRef,
    shapeTextCommitTimerRef,
    pendingShapeTextCommitShapeIdRef,
    shapeTextChainProjectedRef,
    shapeTextProjectionInFlightRef,
    shapeTextEditorRef,
    geometryBlockedPulseKey,
    setGeometryBlockedPulseKey,
    frozenOutlinePulse,
    setFrozenOutlinePulse,
    interactionRef,
    containerRef,
    scaleRef,
    interactionEndTimeRef,
    shapesRef,
    getLinkedTextChainsRef,
    onLinkedTextExcludedFromGroupRef,
    ctrlCycleRef,
    rafIdRef,
    shapeBoundsRef,
    shapePlacementDimsRef,
    multiSelectChromeRef,
    multiSelectSyncPrevShapesRef,
    multiSelectSelectionKeyRef,
    pendingPointerRef,
    panelDimensionsLinkedRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    savedBodyCursorForMultiMoveRef,
    selectedShapeIdsRef,
    onLiveSelectionGeometrySyncRef,
    onLiveShapeGeometryChangeForWrapRef,
    onCornerResizeGestureActiveChangeRef,
    liveWrapSyncRafIdRef,
    getSnappedNudgeItemsRef,
    handlePointerMoveRef,
    handlePointerUpRef,
    handlePointerMoveBridge,
    handlePointerUpBridge,
    promoteAltDragDuplicateRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
  }
}

export type ShapeManagerStateApi = ReturnType<typeof useShapeManagerState>
